/**
 * ProjectContentHead.jsx
 *
 * Displays a project's name and description, with an edit button.
 * The component supports ref forwarding to the root Stack element.
 */

import PropTypes from "prop-types";
import { forwardRef } from "react";
import OpenProjectEditIcon from "@mui/icons-material/EditOutlined";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * Displays a project's name and description, with an edit button.
 * The component supports ref forwarding to the root Stack element.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {function} [props.onEditClick] - Callback triggered when the edit button is clicked.
 * @param {Object} props.project - Project data object.
 * @param {string} [props.project.name] - Name of the project.
 * @param {string} [props.project.description] - Description of the project (may contain HTML).
 * @param {React.Ref<HTMLDivElement>} ref - Forwarded ref to the root Stack element.
 * @returns {JSX.Element} The rendered ProjectContentHead component.
 */
const ProjectContentHead = forwardRef(({
  onEditClick,
  project,
}, ref) => (
  <Stack ref={ref} direction="column">

    <Stack direction="row">
      <Typography
        gutterBottom
        sx={{ fontSize: "28px", fontWeight: 500, m: 0, p: 0 }}
        variant="h5"
      >
        {project?.name}
      </Typography>

      <IconButton
        onClick={onEditClick}
        sx={{
          opacity: 0.4,
          p: "6px",
          "&:hover": { opacity: 1 },
        }}
      >
        <OpenProjectEditIcon />
      </IconButton>
    </Stack>

    <Stack direction="row">
      <Typography
        dangerouslySetInnerHTML={{
          __html: project?.description,
        }}
        gutterBottom
        sx={{
          fontSize: "14px",
          mb: 0,
          pb: "6px",
          pt: 0,
          textTransform: "none",
        }}
        variant="subtitle1"
      />
    </Stack>

  </Stack>
));

ProjectContentHead.propTypes = {
  onEditClick: PropTypes.func,
  project: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
  }),
};

ProjectContentHead.displayName = "ProjectContentHead";

export default ProjectContentHead;
